import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getFoundingMemberStatus, reserveFoundingSeat } from '@/services/foundingApi';
import type { BadgeTier, SubmissionSummary } from '@/types/founding';

// ─── Context Type ───────────────────────────────────────────────

export interface FoundingMemberContextType {
  isFoundingMember: boolean;
  cohortPosition: number | null;
  badgeTier: BadgeTier | null;
  badgeAwardedAt: string | null;
  badgeUpgradedAt: string | null;
  submissionCount: number;
  submissions: SubmissionSummary[];
  limit: number;
  isLoading: boolean;
  isNewUser: boolean;
  refreshStatus: () => Promise<void>;
}

const FoundingMemberContext = createContext<FoundingMemberContextType | null>(null);

// ─── Provider ───────────────────────────────────────────────────

export function FoundingMemberProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const [isFoundingMember, setIsFoundingMember] = useState(false);
  const [cohortPosition, setCohortPosition] = useState<number | null>(null);
  const [badgeTier, setBadgeTier] = useState<BadgeTier | null>(null);
  const [badgeAwardedAt, setBadgeAwardedAt] = useState<string | null>(null);
  const [badgeUpgradedAt, setBadgeUpgradedAt] = useState<string | null>(null);
  const [submissionCount, setSubmissionCount] = useState(0);
  const [submissions, setSubmissions] = useState<SubmissionSummary[]>([]);
  const [limit, setLimit] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);

  // Fetch founding member status — defined inline in effect to avoid
  // unstable callback reference causing infinite re-render loops.
  // (session object changes identity on every TOKEN_REFRESHED event)
  useEffect(() => {
    if (!user) {
      // Reset state on logout
      setIsFoundingMember(false);
      setCohortPosition(null);
      setBadgeTier(null);
      setBadgeAwardedAt(null);
      setBadgeUpgradedAt(null);
      setSubmissionCount(0);
      setSubmissions([]);
      setIsLoading(false);
      setIsNewUser(false);
      return;
    }

    let cancelled = false;

    async function fetchStatus() {
      try {
        const status = await getFoundingMemberStatus();
        if (cancelled) return;

        setIsFoundingMember(status.isFoundingMember);
        setCohortPosition(status.cohortPosition);
        setBadgeTier(status.badgeTier);
        setBadgeAwardedAt(status.badgeAwardedAt);
        setBadgeUpgradedAt(status.badgeUpgradedAt ?? null);
        setSubmissionCount(status.submissionCount);
        setSubmissions(status.submissions);
        setLimit(status.limit ?? 100);

        // If user is not a founding member yet, attempt to reserve a seat
        if (!status.isFoundingMember) {
          try {
            const seatResult = await reserveFoundingSeat();
            if (cancelled) return;
            if (seatResult.isFoundingMember) {
              setIsFoundingMember(true);
              setCohortPosition(seatResult.cohortPosition ?? null);
              setIsNewUser(true);
              if (seatResult.limit) setLimit(seatResult.limit);
            }
          } catch {
            // Expected when seats are full or token unavailable — silently ignore
          }
        }
      } catch {
        // Expected when user has no auth token yet — silently ignore
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchStatus();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Only re-run when user identity changes, not on token refresh

  const refreshStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const status = await getFoundingMemberStatus();
      setIsFoundingMember(status.isFoundingMember);
      setCohortPosition(status.cohortPosition);
      setBadgeTier(status.badgeTier);
      setBadgeAwardedAt(status.badgeAwardedAt);
      setBadgeUpgradedAt(status.badgeUpgradedAt ?? null);
      setSubmissionCount(status.submissionCount);
      setSubmissions(status.submissions);
      setLimit(status.limit ?? 100);
    } catch {
      // silently ignore
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value: FoundingMemberContextType = {
    isFoundingMember,
    cohortPosition,
    badgeTier,
    badgeAwardedAt,
    badgeUpgradedAt,
    submissionCount,
    submissions,
    limit,
    isLoading,
    isNewUser,
    refreshStatus,
  };

  return <FoundingMemberContext.Provider value={value}>{children}</FoundingMemberContext.Provider>;
}

// ─── Hook ───────────────────────────────────────────────────────

export function useFoundingMember() {
  const context = useContext(FoundingMemberContext);
  if (!context) {
    throw new Error('useFoundingMember must be used within FoundingMemberProvider');
  }
  return context;
}
